import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { athleteAPI, stripeAPI } from '../services/api';
import { getRequiredPlan, hasPlanOrHigher, isSubscriptionEntitled } from '../utils/subscriptionUtils';
import { Athlete } from '../types/mongodb';

interface SubscriptionStatus {
  role: 'athlete' | 'coach';
  subscription?: any;
  athletes?: Array<{ id: string; name: string; subscription: any }>;
}

export function useSubscriptionAccess() {
  const { user } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [accessibleAthletes, setAccessibleAthletes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadSubscriptionStatus();
  }, [user]);

  const loadSubscriptionStatus = async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      const status = await stripeAPI.getSubscriptionStatus();
      setSubscriptionStatus(status);

      if (status.role === 'coach' && status.athletes) {
        // Store only entitled athletes for access checking / filtering.
        // (Subscription "plan" can remain set even after cancellation; entitlement is derived from status + endDate.)
        const entitledIds = status.athletes
          .filter((a: any) => isSubscriptionEntitled(a.subscription))
          .map((a: any) => a.id);
        setAccessibleAthletes(entitledIds);
      } else if (status.role === 'athlete' && status.subscription) {
        setAccessibleAthletes(isSubscriptionEntitled(status.subscription) ? [user.athleteId || ''] : []);
      }
    } catch (error) {
      console.error('Error loading subscription status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const canAccessPage = (pagePath: string): boolean => {
    if (!subscriptionStatus) return false;
    
    const requiredPlan = getRequiredPlan(pagePath);
    if (requiredPlan === 'free') return true;

    if (subscriptionStatus.role === 'athlete') {
      const sub = subscriptionStatus.subscription;
      if (!isSubscriptionEntitled(sub)) return false;
      const athletePlan = sub?.plan || 'free';
      return hasPlanOrHigher(athletePlan, requiredPlan);
    } else {
      // Coach: can access if at least one athlete has the required plan
      // Check if any athlete has the required plan or higher
      if (subscriptionStatus.athletes) {
        return subscriptionStatus.athletes.some((athlete: any) => {
          const sub = athlete.subscription;
          if (!isSubscriptionEntitled(sub)) return false;
          const athletePlan = sub?.plan || 'free';
          return hasPlanOrHigher(athletePlan, requiredPlan);
        });
      }
      return false;
    }
  };

  return {
    subscriptionStatus,
    accessibleAthletes,
    canAccessPage,
    isLoading
  };
}

